import {
  Box,
  Flex,
  Text,
  InputGroup,
  Input,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { typeguards as finleyTypeguards } from '@finley-technologies/finley-types';

import {
  SaveIconButton,
  EditIconButton,
  SearchIconButton,
  XIconButton,
  DeleteIconButton,
} from 'packages/common-frontend/components/IconButton';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { FiPlusCircle } from 'react-icons/fi';

type EditColumnNamesModalProps = {
  initColumnNames: string[];
  isOpen: boolean;
  onCancel: () => void;
  onSave: (newColumnNames: string[]) => void;
};
const EditColumnNamesModal = ({
  initColumnNames,
  isOpen,
  onCancel,
  onSave,
}: EditColumnNamesModalProps) => {
  const [columnNamesState, setColumnNamesState] = useState<string[]>(
    initColumnNames,
  );
  const [editName, setEditName] = useState<string>('');
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const onValidateAndSave = (): void => {
    setErrorMessage('');

    const dupNames: string[] = [];
    const seenNames = new Set<string>();
    for (const name of columnNamesState) {
      if (seenNames.has(name)) {
        dupNames.push(name);
      } else {
        seenNames.add(name);
      }
    }

    if (dupNames.length > 0) {
      setErrorMessage(`Duplicate names: ${dupNames.join(', ')}`);
      return;
    }

    onSave(columnNamesState);
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="md"
    >
      <ModalOverlay />
      <ModalContent p="2">
        <ModalHeader pb="1">Edit Athena Column Names</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Text fontSize="md" mb="4">
            Please derive column names by executing the query itself, as this
            ensures the right columns are utilized. Only utilize these manual
            edits when truly needed
          </Text>

          <Button
            onClick={() => {
              setColumnNamesState((prev) => [...prev, 'placeholder']);
            }}
            leftIcon={<FiPlusCircle />}
            mb="2"
          >
            Add New Column
          </Button>

          <Flex alignItems="center" p="2">
            <Text w="72px" mr="1">
              {editIdx !== null ? `Edit #${editIdx + 1}0:` : 'Edit:'}
            </Text>
            <InputGroup w="52" mr="2">
              <Input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </InputGroup>
            <SaveIconButton
              ariaLabel="save name"
              label="Save Name"
              onClick={() => {
                setColumnNamesState((prev) => {
                  if (editIdx === null) {
                    return prev;
                  }
                  const newArray = [...prev];
                  newArray[editIdx] = editName.trim();
                  return newArray;
                });
                setEditName('');
                setEditIdx(null);
              }}
              isDisabled={editIdx === null}
            />
            <XIconButton
              ariaLabel="close edit"
              label="Close Edit"
              onClick={() => {
                setEditName('');
                setEditIdx(null);
              }}
              isDisabled={editIdx === null}
            />
          </Flex>

          <Divider my="3" />

          <Box mb="4">
            {columnNamesState.map((name, idx) => (
              <Flex
                key={`${name}-${uuidv4()}`}
                alignItems="center"
                px="2"
                {...(editIdx === idx ? { border: '1px', rounded: 'md' } : {})}
              >
                <Text w="6" mr="2">
                  {idx + 1}.
                </Text>
                <Text w="80" mr="2">
                  {name}
                </Text>

                <EditIconButton
                  ariaLabel="edit name"
                  label="Edit Name"
                  onClick={() => {
                    setEditName(name);
                    setEditIdx(idx);
                  }}
                  isDisabled={editIdx !== null}
                />
                <DeleteIconButton
                  ariaLabel="delete name"
                  label="Delete Name"
                  onClick={() => {
                    setColumnNamesState((prev) =>
                      prev.filter((_unused, loopIdx) => loopIdx !== idx),
                    );
                  }}
                  isDisabled={editIdx !== null}
                />
              </Flex>
            ))}
          </Box>

          {errorMessage && (
            <Alert status="error" mb="7" rounded="md">
              <AlertIcon />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Flex justifyContent="center">
            <Button type="button" mr="5" onClick={() => onValidateAndSave()}>
              Update
            </Button>
            <Button onClick={() => onCancel()}>Close</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type ColumnNamesContainerProps = {
  wipConfig?: WipConfig;
  columnNames: string[];
  configPropertyKey: string;
  executeQueryMethod: () => void;
  isExecuteQueryLoading?: boolean;
};
export const ColumnNamesContainer = ({
  columnNames,
  wipConfig,
  configPropertyKey,
  executeQueryMethod,
  isExecuteQueryLoading,
}: ColumnNamesContainerProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const onSave = (newColumnNames: string[]): void => {
    if (!wipConfig?.isMutateAllowed(configPropertyKey)) {
      return;
    }

    const wipRecurringQuery = getIn(
      wipConfig.formProps.values,
      configPropertyKey,
    );
    finleyAssert(
      finleyTypeguards.creditAgreement.isAthenaConfigRecurringQuery(
        wipRecurringQuery,
      ),
      `Retrieved non-Athena Recurring Query structure for [key:${configPropertyKey}]`,
    );

    const recurringQueryCopy = _.cloneDeep(wipRecurringQuery);
    recurringQueryCopy.columnNames = newColumnNames;
    wipConfig.formProps.setFieldValue(configPropertyKey, recurringQueryCopy);
  };

  return (
    <Box mb="5">
      <Flex alignItems="center" mb="2">
        <Text fontWeight="bold" mr="2">
          Column Names
        </Text>

        {wipConfig?.isMutateAllowed(configPropertyKey) && (
          <>
            {/*
             * Only allowing this on Dev to help with setup / QA process.
             * Otherwise, want to force users to rely on executing query instead
             */}
            {commonUtils.EnvUtils.isDevelopment() && (
              <EditIconButton
                ariaLabel="edit column names"
                label="Edit Column Names"
                onClick={() => setIsEditModalOpen(true)}
              />
            )}
            <SearchIconButton
              ariaLabel="execute query"
              label="Execute Query"
              onClick={executeQueryMethod}
              isLoading={isExecuteQueryLoading}
            />
          </>
        )}
      </Flex>

      <Box border="1px" borderColor="gray.700" px="3" py="5">
        {columnNames.length > 0 ? (
          columnNames.map((columnName, loopIdx) => (
            <Text
              key={columnName}
              mb={loopIdx < columnNames.length - 1 ? 3 : 0}
            >
              {loopIdx + 1}. {columnName}
            </Text>
          ))
        ) : (
          <Text>Remember to set column names after configuring SQL query</Text>
        )}
      </Box>

      {isEditModalOpen && (
        <EditColumnNamesModal
          initColumnNames={columnNames}
          isOpen={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          onSave={onSave}
        />
      )}
    </Box>
  );
};
